import type { SlotName } from "@/lib/ads/zones";

/**
 * Display-slot -> affiliate-engine surface for the "network ad did not fill"
 * fallback (`AdSlotClient`).
 *
 * Observed 2026-09-28/29 (PostHog): job-detail sessions with
 * `ad_slot_unfilled reason=timeout` showed NO ad at all — the AdSense unit
 * timed out and nothing took its place. The slot now hands over to the
 * affiliate engine (`NativeAd`).
 *
 * The surface is `<registered base>:display_fallback`, NOT a new registry
 * entry. The engine gates on the part before the first `:`
 * (`settings.surfaces[surface.split(":")[0]]`), so each fallback is switched
 * on/off by the payload row of a base surface that already exists, with no code
 * constant shadowing it (the PostHog payload is the sole switch), and the
 * `:display_fallback` suffix keeps its impressions/clicks separable from that
 * base surface's own in PostHog and D1.
 */
const FALLBACK_BASE: Record<SlotName, string> = {
	"blog-top": "blog_top",
	"blog-bottom": "blog_bottom",
	"feed-sidebar": "job_sidebar",
	"category-footer": "category_footer",
	"job-detail-bottom": "job_sidebar",
	"job-detail-mid": "job_sidebar",
	"feed-inline-display": "feed_inline_display",
};

export function displayFallbackSurface(slot: string): string {
	return `${FALLBACK_BASE[slot as SlotName] ?? "job_sidebar"}:display_fallback`;
}

/**
 * Static Tailwind classes for the reserved-height values `SLOT_DIMENSIONS`
 * uses (a dynamic `min-h-[${n}px]` would be purged). The fallback reuses the
 * slot's own reserved height so the swap from the network unit to the
 * affiliate card causes no layout shift.
 */
const MIN_HEIGHT_CLASS: Record<number, string> = {
	100: "min-h-[100px]",
	250: "min-h-[250px]",
	280: "min-h-[280px]",
	600: "min-h-[600px]",
};

export function reservedMinHeightClass(minHeight: number): string | undefined {
	return MIN_HEIGHT_CLASS[minHeight];
}
